import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from '../css/clinical.module.css';
import clinicalData from '../data/clinicalCases.json';
import waves from '../img/background.svg';

const ClinicalCasesSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [mainCategory, setMainCategory] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [condition, setCondition] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [onlySegmentation, setOnlySegmentation] = useState(false);
  const [allCases, setAllCases] = useState([]);

  // Извлечение всех кейсов из данных
  useEffect(() => {
    const extractCases = (obj, path = '', mainCategory = '') => {
      let cases = [];
      
      if (Array.isArray(obj)) {
        obj.forEach(caseItem => {
          if (caseItem && typeof caseItem === 'object' && caseItem.id) {
            const pathParts = path.split('.');
            cases.push({
              ...caseItem,
              path,
              mainCategory,
              specialty: pathParts[1] || 'Other',
              subspecialty: pathParts[2] || 'Other'
            });
          }
        });
      } else if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach(key => {
          const newPath = path ? `${path}.${key}` : key;
          const newMainCategory = mainCategory || key;
          cases = [...cases, ...extractCases(obj[key], newPath, newMainCategory)];
        });
      }
      
      return cases;
    };

    setAllCases(extractCases(clinicalData));
  }, []);

  // Форматирование ID с ведущими нулями
  const formatId = (id) => {
    return String(id).padStart(4, '0');
  };

  // Проверка, начинается ли ID с 7 (с сегментацией)
  const hasSegmentation = (id) => {
    const formattedId = formatId(id);
    return formattedId.startsWith('7');
  };

  // Получение уникальных значений для фильтров
  const mainCategories = useMemo(() => 
    [...new Set(allCases.map(c => c.mainCategory))].sort(),
    [allCases]
  );

  const specialties = useMemo(() => 
    mainCategory 
      ? [...new Set(allCases
          .filter(c => c.mainCategory === mainCategory)
          .map(c => c.specialty))].sort()
      : [],
    [allCases, mainCategory]
  );

  const subspecialties = useMemo(() => 
    specialty 
      ? [...new Set(allCases
          .filter(c => c.mainCategory === mainCategory && c.specialty === specialty)
          .map(c => c.subspecialty))].sort()
      : [],
    [allCases, mainCategory, specialty]
  );

  // Сброс зависимых фильтров при изменении родительского
  useEffect(() => {
    setSpecialty('');
    setCondition('');
  }, [mainCategory]);

  useEffect(() => {
    setCondition('');
  }, [specialty]);

  // Фильтрация кейсов
  const filteredCases = useMemo(() => {
    return allCases.filter(caseItem => {
      const formattedId = formatId(caseItem.id);
      
      const matchesSearch = !searchTerm || 
        caseItem.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        formattedId.includes(searchTerm) ||
        caseItem.id.toString().includes(searchTerm);
      
      const matchesMainCategory = !mainCategory || caseItem.mainCategory === mainCategory;
      const matchesSpecialty = !specialty || caseItem.specialty === specialty;
      const matchesCondition = !condition || caseItem.subspecialty === condition;
      const matchesSegmentation = !onlySegmentation || hasSegmentation(caseItem.id);
      
      return matchesSearch && matchesMainCategory && matchesSpecialty && matchesCondition && matchesSegmentation;
    });
  }, [allCases, searchTerm, mainCategory, specialty, condition, onlySegmentation]);

  // Сортировка кейсов
  const sortedCases = useMemo(() => {
    const cases = [...filteredCases];
    
    cases.sort((a, b) => {
      switch (sortBy) {
        case 'id-desc':
          return b.id - a.id;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'category':
          return a.mainCategory.localeCompare(b.mainCategory);
        default:
          return a.id - b.id;
      }
    });
    
    return cases;
  }, [filteredCases, sortBy]);

  // Очистка всех фильтров
  const clearAllFilters = () => {
    setSearchTerm('');
    setMainCategory('');
    setSpecialty('');
    setCondition('');
    setSortBy('id');
    setOnlySegmentation(false);
  };

  // Форматирование текста опций
  const formatOptionText = (text) => {
    return text
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Получение класса для бейджа категории
  const getCategoryBadgeClass = (category) => {
    const categoryClass = category.replace(/\s+/g, '');
    return `${styles.categoryBadge} ${styles[categoryClass] || ''}`;
  };

  return (
    <div className={styles.wrapper}>
      <div 
        className={styles.backgroundSvg}
        style={{ backgroundImage: `url(${waves})` }}
      />
      
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>🏥 Clinical Cases Database</h1>
          <p>Simplified Medical Cases Search System</p>
          <Link to="/" className={styles.backBtn}>
            ← Back to Menu
          </Link>
        </div>

        <div className={styles.searchSection}>
          <div className={styles.searchControls}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="🔍 Search cases by title or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className={styles.clearBtn} onClick={clearAllFilters}>
              🗑️ Clear All
            </button>
          </div>

          <div className={styles.filtersSection}>
            <div className={`${styles.filterGroup} ${mainCategory ? styles.active : ''}`}>
              <h3>🏥 Main Category</h3>
              <select
                className={styles.filterSelect}
                value={mainCategory}
                onChange={(e) => setMainCategory(e.target.value)}
              >
                <option value="">Select Main Category</option>
                {mainCategories.map(cat => (
                  <option key={cat} value={cat}>
                    {formatOptionText(cat)}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${styles.filterGroup} ${!mainCategory ? styles.disabled : ''} ${specialty ? styles.active : ''}`}>
              <h3>🔬 Specialty</h3>
              <select
                className={styles.filterSelect}
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                disabled={!mainCategory}
              >
                <option value="">Select Specialty</option>
                {specialties.map(spec => (
                  <option key={spec} value={spec}>
                    {formatOptionText(spec)}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${styles.filterGroup} ${!specialty ? styles.disabled : ''} ${condition ? styles.active : ''}`}>
              <h3>🎯 Condition</h3>
              <select
                className={styles.filterSelect}
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                disabled={!specialty}
              >
                <option value="">Select Condition</option>
                {subspecialties.map(sub => (
                  <option key={sub} value={sub}>
                    {formatOptionText(sub)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Чекбокс для сегментации */}
          <div className={styles.checkboxSection}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={onlySegmentation}
                onChange={(e) => setOnlySegmentation(e.target.checked)}
              />
              <span className={styles.checkboxText}>
                Only with Segmentation
              </span>
            </label>
          </div>
        </div>

        <div className={styles.resultsSection}>
          <div className={styles.resultsHeader}>
            <div className={styles.resultsCount}>
              {sortedCases.length} case{sortedCases.length !== 1 ? 's' : ''} found
            </div>
            <div className={styles.sortControls}>
              <label htmlFor="sortSelect">Sort by:</label>
              <select
                id="sortSelect"
                className={styles.sortSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="id">ID (ascending)</option>
                <option value="id-desc">ID (descending)</option>
                <option value="title">Title (A-Z)</option>
                <option value="title-desc">Title (Z-A)</option>
                <option value="category">Category</option>
              </select>
            </div>
          </div>

          <div className={styles.resultsTable}>
            <div className={styles.tableHeader}>
              <div className={styles.tableHeaderRow}>
                <div>ID</div>
                <div>Clinical Case Title</div>
                <div className={styles.categoryHeader}>Category</div>
                <div className={styles.categoryHeader}>Specialty</div>
                <div className={styles.categoryHeader}>Condition</div>
              </div>
            </div>
            
            <div className={styles.tableBody}>
              {sortedCases.length === 0 ? (
                <div className={styles.noResults}>
                  <h3>🔍 No cases found</h3>
                  <p>Try adjusting your search criteria.</p>
                </div>
              ) : (
                sortedCases.map((caseItem, index) => (
                  <div
                    key={`${caseItem.id}-${index}`}
                    className={styles.tableRow}
                    style={{ animationDelay: `${Math.min(index * 0.05, 1)}s` }}
                  >
                    <div className={styles.caseId}>{formatId(caseItem.id)}</div>
                    <div className={styles.caseTitle}>{caseItem.title}</div>
                    <div className={styles.caseBadgeCell}>
                      <span className={getCategoryBadgeClass(caseItem.mainCategory)}>
                        {formatOptionText(caseItem.mainCategory)}
                      </span>
                    </div>
                    <div className={styles.caseBadgeCell}>
                      <span className={`${styles.infoBadge} ${getCategoryBadgeClass(caseItem.mainCategory)}`}>
                        {formatOptionText(caseItem.specialty)}
                      </span>
                    </div>
                    <div className={styles.caseBadgeCell}>
                      <span className={`${styles.infoBadge} ${getCategoryBadgeClass(caseItem.mainCategory)}`}>
                        {formatOptionText(caseItem.subspecialty)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalCasesSearch;
